import type { BlockKind } from '@/blocks/domain/types';
import { BLOCK_KINDS, LEGACY_BLOCK_KIND_ALIASES } from '@/blocks/domain/types';

export interface ResolvedBlockKind {
  /**
   * The kind the app edits and renders the block as. For a supported kind this is its canonical
   * value; for a legacy alias (`project_details`) it is the kind it is handled as (`scope_fields`).
   * For an UNSUPPORTED kind it is only a type-level placeholder — callers must check
   * `isSupportedKind` before using it for grouping, labels, content mapping or editing.
   */
  kind: BlockKind;
  /** The kind exactly as the API stores it (trimmed). Every PUT sends this back, never `kind`. */
  rawKind: string;
  /** False when the app has no editor or renderer for `rawKind`. */
  isSupportedKind: boolean;
}

/** Type-level placeholder for an unsupported kind. Deliberately not `header` (that grouped it under "Letterhead"). */
const UNSUPPORTED_PLACEHOLDER_KIND: BlockKind = 'custom';

/**
 * Resolves the API's free-form `kind` / `blockType` string to the app's `BlockKind`, without ever
 * coercing an unknown value to `header` (LLAMS-610 BL-10). `BlockKind` has no `unknown` member by
 * design: an unsupported kind is carried as `rawKind` plus `isSupportedKind: false`, and the editor
 * stops before `mapContent`.
 */
export function resolveBlockKind(raw: unknown): ResolvedBlockKind {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return { kind: UNSUPPORTED_PLACEHOLDER_KIND, rawKind: '', isSupportedKind: false };
  }

  const rawKind = raw.trim();
  // Accept wire values like "Customer block" or "Line items table".
  const normalized = rawKind.toLowerCase().replace(/[\s-]+/g, '_');

  const alias = LEGACY_BLOCK_KIND_ALIASES.get(normalized);
  if (alias) return { kind: alias, rawKind, isSupportedKind: true };

  if ((BLOCK_KINDS as readonly string[]).includes(normalized)) {
    return { kind: normalized as BlockKind, rawKind, isSupportedKind: true };
  }

  return { kind: UNSUPPORTED_PLACEHOLDER_KIND, rawKind, isSupportedKind: false };
}
